import i18n from 'i18next'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { pseudoLocalizeString } from './pseudoLocalizeString'
import { useLocalLanguage } from './useLocalLanguage'

export type CldrPluralForms = {
  zero?: string
  one?: string
  two?: string
  few?: string
  many?: string
  other?: string
}

export type LocalizedString = {
  [lang: string]: string | CldrPluralForms | undefined
}

export function translate(value: LocalizedString | undefined, locale: string | undefined): string | undefined {
  if (!value) {
    return undefined
  }

  const usedLocale = locale === 'dev' ? 'en' : (locale ?? 'en')
  if (usedLocale === 'pseudo') {
    const raw = translate(value, 'en')
    return raw ? pseudoLocalizeString(raw) : raw
  }

  const string = value[usedLocale]

  if (typeof string === 'string') {
    return string
  }

  if (typeof string === 'object' && string !== null) {
    return string.other ?? string[Object.keys(string)[0] as keyof CldrPluralForms]
  }

  return undefined
}

export function translateBestEffort(value: LocalizedString | undefined, ...locales: (string | undefined)[]): string | undefined {
  if (!value) {
    return undefined
  }

  const usedLocales = locales.length === 0 ? [i18n.language] : locales
  for (const locale of usedLocales) {
    if (!locale) {
      continue
    }

    const translation = translate(value, locale)
    if (translation) {
      return translation
    }
  }

  const definedLocales = Object.keys(value)
  if (definedLocales.length === 0) {
    return undefined
  }

  return translate(value, definedLocales[0])
}

export function useMissingTranslationLabel() {
  const { t } = useTranslation('shared')
  return t('common.localization.missing', 'Missing translation')
}

export function useTranslated(value: LocalizedString, ...preferredFallBackLocales: string[]): { translation: string | undefined; missing: boolean } {
  const language = useLocalLanguage()

  return useMemo(() => {
    const targetTranslation = language ? translate(value, language) : null

    if (targetTranslation) {
      return { translation: targetTranslation, missing: false }
    }

    const locales = language ? [language, ...preferredFallBackLocales] : preferredFallBackLocales
    const fallbackTranslation = translateBestEffort(value, ...locales)
    return { translation: fallbackTranslation, missing: true }
  }, [value, language, preferredFallBackLocales])
}
